import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { format } from 'date-fns';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { CourseDetail } from '@eliadmin/core/src/api/courses/coursers-api';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { getCourseExternalLink } from './course-external-link';
import { CourseContactActions } from './course-contact-actions';

const getCourseTypeByTypeId = (id: string | number) => {
  const idString = id.toString();

  if (idString === '1') {
    return 'Doplnkový kurz';
  }
  if (idString === '2') {
    return 'Akreditovaný kurz';
  }
  if (idString === '3') {
    return 'Rekvalifikačný kurz';
  }

  return idString;
};

export const CourseDetailPage = ({ course }: { course: CourseDetail }) => {
  const courseCategories = course.categories || [];
  const productPrice = getProductPriceDisplay(course.product);
  const externalLink = getCourseExternalLink(course);

  const schoolData = course?.customer?.job_portal_school;

  return (
    <>
      <Container className="flex flex-col gap-8 pb-8">
        <div className="flex-start left-0 mb-4 flex w-full items-center justify-center bg-white px-4 md:mb-6 md:h-8 md:px-2">
          <p className="w-full max-w-[1171px] text-sm">
            <Link href={'/'}>Domov</Link> / <Link href={'/kurzy'}>Vzdelávacie kurzy</Link> /{' '}
            {course.name}
          </p>
        </div>

        <div className="flex flex-col items-start justify-center md:h-[294px] md:flex-row md:justify-between md:gap-8">
          <div className="flex flex-col md:flex-row">
            <Image
              src={course.image?.url || '/images/placeholder.png'}
              alt={resolveImageAlt(course?.image, course?.name)}
              width={252}
              height={252}
              sizes="252px"
              priority
              fetchPriority="high"
              className="h-[252px] w-[252px] rounded-lg object-cover"
            />
            <div className="h-full items-start justify-start gap-4 py-8 md:flex md:flex-col md:gap-4 md:p-8">
              <div className="flex flex-wrap items-center gap-2 pb-4 md:pb-0">
                {courseCategories.map((category) => (
                  <div
                    key={category.id}
                    className="cursor-default whitespace-nowrap rounded-[4px] bg-primary/10 px-2 py-1 text-sm text-primary md:text-base"
                  >
                    {category.name}
                  </div>
                ))}
              </div>
              <h1 className="text-2xl font-bold">{course.name}</h1>
              <div>
                {course.metadata?.courseType?.filter((course: any) => course.id).length > 0 && (
                  <p className="text-lg">
                    Typ:{' '}
                    {course.metadata?.courseType
                      ?.filter((type: any) => !!(type?.id || type?.name))
                      .map((type: any) =>
                        type?.name
                          ? type?.name
                          : type?.id
                            ? getCourseTypeByTypeId(type.id)
                            : type.id?.toString?.(),
                      )
                      .join(', ') || 'Nešpecifikované'}
                  </p>
                )}
                {(course.date_start || course.date_stop) && (
                  <p className="text-lg">
                    {course.date_start && course.date_stop
                      ? `${format(new Date(course.date_start), 'dd.MM.yyyy')} – ${format(
                          new Date(course.date_stop),
                          'dd.MM.yyyy',
                        )}`
                      : course.date_start
                        ? format(new Date(course.date_start), 'dd.MM.yyyy')
                        : course.date_stop
                          ? format(new Date(course.date_stop), 'dd.MM.yyyy')
                          : '---'}
                  </p>
                )}
                {course.metadata?.learningMethod?.length > 0 && (
                  <p className="text-lg">
                    <strong>Forma štúdia:</strong> {course.metadata?.learningMethod?.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-[#D8D8DB]" />

        <div className="flex flex-col gap-8 md:flex-row">
          <div className="flex flex-col gap-4 md:w-2/3">
            <div className="flex flex-col gap-1">
              <h2 className="mb-1 mt-4 text-lg font-semibold text-primary md:text-xl ">
                Popis vzdelávacieho kurzu
              </h2>
              <HtmlRenderer html={course.description} />
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="mb-1 mt-4 text-lg font-semibold text-primary md:text-xl">
                Obsah vzdelávacieho kurzu
              </h2>
              <HtmlRenderer html={course.short_description} />
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="mb-1 mt-4 text-lg font-semibold text-primary md:text-xl">
                Pre koho je vzdelávací kurz určený?
              </h2>
              <HtmlRenderer html={course.metadata?.targetAudience || '—'} />
            </div>

            {course.metadata?.instructors?.length > 0 && (
              <div>
                <h3 className="mb-1 mt-4 text-xl font-semibold text-primary">Lektori</h3>
                <ul className="list-disc pl-6">
                  {course.metadata?.instructors.map((instructor: any, index: any) => (
                    <li key={index}>{instructor.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex max-h-fit w-full flex-col items-start gap-4 rounded-lg bg-[#f3f3f3] p-6 text-sm md:max-w-[25vw] md:grid-cols-1">
            <p>
              <strong>Typ vzdelávacieho kurzu:</strong>{' '}
              {course.metadata?.courseType
                ?.filter((type: any) => !!(type?.id || type?.name))
                ?.map((type: any) =>
                  type?.name
                    ? type?.name
                    : type?.id
                      ? getCourseTypeByTypeId(type.id)
                      : type.id?.toString?.(),
                )
                .join(', ') || 'Nešpecifikované'}
            </p>
            {!!course.date_start && (
              <p>
                <strong>Začiatok:</strong>{' '}
                {course.date_start ? format(new Date(course.date_start), 'dd.MM.yyyy') : '—'}
              </p>
            )}
            {course.date_stop && (
              <p>
                <strong>Koniec:</strong>{' '}
                {course.date_stop ? format(new Date(course.date_stop), 'dd.MM.yyyy') : '—'}
              </p>
            )}
            {course.metadata?.canBeReimbursed !== undefined && (
              <p>
                <strong>Možnosť preplatenia:</strong>{' '}
                {course.metadata.canBeReimbursed ? 'Áno' : 'Nie'}
              </p>
            )}
            {course.metadata?.requiresExam !== undefined && (
              <p>
                <strong>
                  Vyžaduje sa skúška v odbornej spôsobilosti pre účely podnikania alebo zamestnania:
                </strong>{' '}
                {course.metadata.requiresExam ? 'Áno' : 'Nie'}
              </p>
            )}
            {course.metadata?.learningMethod?.length > 0 && (
              <p>
                <strong>Forma štúdia:</strong> {course.metadata?.learningMethod.join(', ')}
              </p>
            )}
            {course.metadata?.confirmationForm?.length > 0 && (
              <p>
                <strong>Forma potvrdenia:</strong> {course.metadata?.confirmationForm.join(', ')}
              </p>
            )}
            <p className="w-full text-end text-xl text-primary">
              <strong>Cena:</strong>{' '}
              <span className="font-extrabold">
                {productPrice.priceOnRequest
                  ? 'Cena na vyžiadanie'
                  : (productPrice.current ?? 0) === 0
                    ? 'Zdarma'
                    : formatPrice(productPrice.current, '€')}
              </span>
            </p>
            {externalLink && (
              <Button
                asChild
                className="h-auto min-h-12 w-full gap-2 whitespace-normal px-4 py-3 text-center"
              >
                <a href={externalLink.value} target="_blank" rel="noopener noreferrer">
                  <span>Pozrieť kurz / e-book</span>
                  <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
                </a>
              </Button>
            )}
          </div>
        </div>
        {schoolData && (
          <div className="flex flex-col gap-4">
            <div className="flex w-full items-start gap-4 rounded-lg border border-border p-6">
              {schoolData.image?.url && (
                <Image
                  src={schoolData?.image?.url || ''}
                  alt={resolveImageAlt(schoolData?.image, schoolData?.name)}
                  width={96}
                  height={96}
                  className="h-24 w-24 rounded object-cover"
                />
              )}
              <div className="flex flex-col gap-1">
                <p className="text-lg font-semibold">{schoolData.name}</p>
                {schoolData.www && (
                  <a
                    href={
                      (schoolData.www?.startsWith('http')
                        ? schoolData.www
                        : `https://${schoolData.www}`) as string
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {schoolData.www}
                  </a>
                )}
                <div className="text-sm">
                  {(schoolData.address ||
                    schoolData.street_number ||
                    schoolData.postcode ||
                    schoolData.city ||
                    schoolData.country?.name) && (
                    <p>
                      {[
                        [schoolData.address, schoolData.street_number].filter(Boolean).join(' '),
                        [schoolData.postcode, schoolData.city].filter(Boolean).join(' '),
                        schoolData.country?.name,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <CourseContactActions key={course.id} courseId={course.id} />
      </Container>
    </>
  );
};

export default CourseDetailPage;
